// Can 68 step 3: a change to what Neucleos charges, as its card shows it: every line the World stored (each price old → new with
// its cost, page and date, voice, the rule, what stays), who asked and why, who approved it once applied, the exact change the
// approval is bound to and when the card expires (or why it is closed). Can 47: the Desk (apps/web commercialChangeClient,
// gateInstancesClient) and Pocket (apps/mobile cloudConversationGate) show it with these same rows; Pocket reads the stored change
// from the approval the World hands it (backend pocketCloudDoors.waitingApprovals commercialChange).
import * as Schema from "effect/Schema";

const whenOf = (ms: number) =>
  new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(ms);

const text = Schema.String.check(Schema.isNonEmpty());
const change = Schema.Struct({
  schemaVersion: Schema.Literal("commercial-change-v1"),
  changeKey: text,
  // Any stored kind (a route publication as well as the switch-over): the card shows its title and lines.
  kind: text,
  title: Schema.optionalKey(Schema.String),
  status: Schema.Literals(["proposed", "applied"]),
  proposedAt: Schema.Number,
  expiresAt: Schema.Number,
  reason: Schema.String,
  // Can 68: who asked for the change and, once applied, who approved it, by name only (null: not known).
  requestedBy: Schema.optionalKey(Schema.NullOr(Schema.String)),
  approvedBy: Schema.optionalKey(Schema.NullOr(Schema.String)),
  lines: Schema.Array(Schema.Array(Schema.String)),
  digest: text,
  appliedAt: Schema.optionalKey(Schema.Number),
  effectiveAt: Schema.optionalKey(Schema.Number),
  // The receipt of an applied change: the person's approval it ran under and every revision it wrote.
  approvalKey: Schema.optionalKey(Schema.String),
  revisionKeys: Schema.optionalKey(Schema.Array(Schema.String)),
  // W-422a1bc9: an applied markup, discount or cost change says what happened to its price at each model's start (and
  // a discount's end), worked out by Neucleos each time it is read. Taken as it comes and read on its own
  // (priceOutcomeLines), so an outcome of a shape this Desk does not know never stops the card from loading.
  priceOutcome: Schema.optionalKey(Schema.Unknown),
  // A change that can no longer be applied, and why (the Orchestrator, 9 Oct 2026): declined (the person's no on its
  // card closed it) or expired, with when. `status` keeps "proposed" for it, the value Desks up to 0.0.200 decode.
  closed: Schema.optionalKey(Schema.Struct({ reason: Schema.String, at: Schema.Number })),
});
export type CommercialChange = typeof change.Type;
export const decodeCommercialChange = Schema.decodeUnknownSync(change);
const outcome = Schema.Struct({ lines: Schema.Array(Schema.Array(Schema.String)) });
const decodeOutcome = Schema.decodeUnknownSync(outcome);

/** The lines of a change's price outcome to show on its card; none when it has none or one of an unknown shape. */
export function priceOutcomeLines(
  value: CommercialChange,
): ReadonlyArray<readonly [string, string]> {
  if (value.priceOutcome === undefined) return [];
  try {
    return decodeOutcome(value.priceOutcome).lines.map(
      ([label, text]) => [label ?? "", text ?? ""] as const,
    );
  } catch {
    return [];
  }
}

/**
 * The card's rows for a prepared change, the same on the Desk's Prices tab and in Cleo's approval: every line the
 * World stored (each price old → new with its cost, page and date, voice, the rule, what stays), then who asked for it
 * and the reason, who approved it once applied, the exact change the approval is bound to and when the card expires.
 */
export function commercialChangeRows(
  value: CommercialChange,
): ReadonlyArray<readonly [string, string]> {
  return [
    ...value.lines.map(([label, text]) => [label ?? "", text ?? ""] as const),
    ...(value.requestedBy ? [["Requested by", value.requestedBy] as const] : []),
    ...(value.reason ? [["Reason given", value.reason] as const] : []),
    ...(value.status === "applied" && value.approvedBy
      ? [
          [
            "Approved by",
            value.appliedAt === undefined
              ? value.approvedBy
              : `${value.approvedBy}, ${whenOf(value.appliedAt)}`,
          ] as const,
        ]
      : []),
    ...priceOutcomeLines(value),
    ["Change", `${value.changeKey} · digest ${value.digest.slice(0, 12)}`] as const,
    value.closed === undefined
      ? ([
          "Card expires",
          whenOf(value.expiresAt),
        ] as const)
      : (["Closed", commercialChangeClosedText(value.closed)] as const),
  ];
}

/** Why a change can no longer be applied, in plain words: declined on its card, or expired. */
export function commercialChangeClosedText(closed: NonNullable<CommercialChange["closed"]>): string {
  const at = whenOf(closed.at);
  return closed.reason === "declined"
    ? `Declined ${at}. It is never applied; asking for it again prepares a new card.`
    : closed.reason === "expired"
      ? `Expired ${at}. Ask for it again to prepare a new card.`
      : `Closed ${at} (${closed.reason}). It can no longer be applied.`;
}

/** What approving does, said once on both cards. */
/**
 * What approving does, by the change's kind (v3: no kind's wording on another's card). The switch-over's prices take
 * effect for new work at once; every kind says what happens if what it rests on has moved. A kind's own timing is in
 * its stored lines (a route's "When it takes effect").
 */
export const commercialChangeConsequence = (kind: string) =>
  kind === "switch-over" ? COMMERCIAL_CHANGE_CONSEQUENCE : COMMERCIAL_CHANGE_UNCHANGED_IF_MOVED;
export const COMMERCIAL_CHANGE_UNCHANGED_IF_MOVED =
  "If anything this card rests on has moved since it was prepared, nothing changes and the card is prepared again.";
export const COMMERCIAL_CHANGE_CONSEQUENCE =
  "New work takes these prices the moment you approve. If any price or cost this card rests on has moved since it was prepared, nothing changes and the card is prepared again.";


export const APPLY_COMMERCIAL_CHANGE_TOOL = "neucleos_apply_commercial_change";
export const COMMERCIAL_CHANGE_HEADLINE = "Change what Neucleos charges";

export interface CommercialChangeCard {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  /** Why Approve is off, or null when the card shows exactly the change the call names and it is still open. */
  readonly blocked: string | null;
}

/**
 * Can 47: the price card for an apply call (its arguments) and the stored change the World gave with its approval, as the Desk's
 * card says it (gateInstancesClient plainCommercialChange). Approve is blocked, with the reason, unless the call names exactly
 * this change (its key and digest, nothing else), the change can be shown whole, and it is still open (not applied, declined or
 * expired).
 */
export function commercialChangeCard(
  args: Record<string, unknown>,
  stored: unknown,
  now: number,
): CommercialChangeCard {
  const blocked = (headline: string, why: string): CommercialChangeCard => ({
    headline,
    rows: [["Why you cannot approve yet", why]],
    blocked: why,
  });
  const { changeKey, digest } = args;
  if (typeof changeKey !== "string" || typeof digest !== "string")
    return blocked(COMMERCIAL_CHANGE_HEADLINE, "This request does not name a prepared change, so it cannot be shown or approved.");
  if (Object.keys(args).some((name) => name !== "changeKey" && name !== "digest"))
    return blocked(COMMERCIAL_CHANGE_HEADLINE, "This request carries more than the prepared change it names, so it cannot be approved. Ask Cleo to prepare it again.");
  let value: CommercialChange;
  try {
    value = decodeCommercialChange(stored);
  } catch {
    return blocked("Cannot show the new prices", "Neucleos did not send this change in a form this app can show whole, so it cannot be approved here.");
  }
  if (value.changeKey !== changeKey || value.digest !== digest)
    return blocked("Cannot show the new prices", "The prepared change is not the one this request names. Ask Cleo to prepare it again.");
  if (value.status === "applied")
    return blocked("These prices are already in effect", "This change was already applied; there is nothing left to approve.");
  if (value.closed?.reason === "declined")
    return blocked("This change was declined", "This change was declined, so it is closed and never applied. Ask Cleo again to prepare a new card.");
  if (value.closed !== undefined || value.expiresAt <= now)
    return blocked("Cannot show the new prices", "This prepared change has expired. Ask Cleo to prepare it again.");
  return {
    headline: value.title ?? COMMERCIAL_CHANGE_HEADLINE,
    rows: [...commercialChangeRows(value), ["When you approve", commercialChangeConsequence(value.kind)]],
    blocked: null,
  };
}
